import { FileData } from '../types/forms';

// Purpose: Service for handling file operations and persistence
export const fileService = {
  // Purpose: Stores file data in memory cache for persistence during navigation
  fileCache: new Map<string, FileData[]>(),

  // Purpose: Stores a file in the cache by field ID
  storeFiles: (fieldId: string, files: FileData[]) => {
    // Filter out invalid files that might come from external sources or store
    const validFilesToStore = files.filter((file) => file && file.name && file.contentType);

    if (validFilesToStore.length === 0) {
      // If the intention is to store an empty array (i.e., clear the files for this fieldId),
      // we should remove the entry from the cache.
      fileService.fileCache.delete(fieldId);
      return;
    }

    fileService.fileCache.set(fieldId, validFilesToStore);
  },

  // Purpose: Retrieves files from the cache by field ID
  getFiles: (fieldId: string): FileData[] => {
    const files = fileService.fileCache.get(fieldId) || [];

    // Filter out any invalid files
    const validFiles = files.filter((file) => file && file.name && file.contentType);

    return validFiles;
  },

  // Purpose: Checks if files exist in the cache for a field
  hasFiles: (fieldId: string): boolean => {
    const files = fileService.fileCache.get(fieldId) || [];
    const validFiles = files.filter((file) => file && file.name && file.contentType);
    return validFiles.length > 0;
  },

  // Purpose: Clears files from the cache for a field
  clearFiles: (fieldId: string) => {
    fileService.fileCache.delete(fieldId);
  },

  // Purpose: Serializes file data for storage (removes actual File object)
  serializeFileData: (files: FileData[]): Partial<FileData>[] => {
    return files
      .filter((file) => file && file.name && file.contentType)
      .map((file) => ({
        name: file.name,
        contentType: file.contentType,
      }));
  },

  // Purpose: Creates a placeholder file object when actual File is not available
  createPlaceholderFile: (name: string, contentType: string): File => {
    // Create an empty blob with the correct type
    const blob = new Blob([], { type: contentType });

    // Convert to File object
    return new File([blob], name, { type: contentType });
  },
};
